import { useState } from "react";
import { motion } from "framer-motion";
import { VscClose } from 'react-icons/vsc';
import Spinner from "./Spinner";
import { hapticError } from "../utils/haptics";

// Requires a real-looking address: local part, @, domain with a dot and a 2+ letter TLD.
// Rejects things the browser's built-in type="email" accepts, like "james@company" or "a@b".
const EMAIL_REGEX =
    /^[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9])?@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

const LIMITS = { name: 100, email: 254, message: 5000 };

const validate = ({ user_name, user_email, message }) => {
    const errors = {};

    const name = user_name.trim();
    if (!name) errors.user_name = "Please enter your name.";
    else if (name.length < 2) errors.user_name = "Name must be at least 2 characters.";

    const email = user_email.trim();
    if (!email) errors.user_email = "Please enter your email address.";
    else if (email.includes("..") || !EMAIL_REGEX.test(email))
        errors.user_email = "Enter a valid email, like james@yourcompany.com.";

    const msg = message.trim();
    if (!msg) errors.message = "Please tell me how I can help.";
    else if (msg.length < 10) errors.message = "Add a little more detail (at least 10 characters).";

    return errors;
};

const FormModal = ({ openBtn, sendEmail, form, isLoading }) => {
    const [values, setValues] = useState({ user_name: "", user_email: "", message: "" });
    const [errors, setErrors] = useState({});

    const animateFrom = { opacity: 0, y: '-100%' }
    const animateTo = { opacity: 1, y: 0 }
    const animateClose = { opacity: 0, y: '100%' }

    const dropIns = {
        open: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.1,
                type: 'spring',
                damping: 25,
                stiffness: 500
            }
        },
        closed: {
            opacity: 0.2,
            y: "100%",
            transition: {
                delay: 1,
                duration: 1
            }
        }
    }

    // Clears a field's error as soon as the user starts fixing it
    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    // Validates a single field when the user leaves it
    const handleBlur = (e) => {
        const { name } = e.target;
        const fieldError = validate(values)[name];
        if (fieldError) setErrors((prev) => ({ ...prev, [name]: fieldError }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isLoading) return;

        const found = validate(values);
        if (Object.keys(found).length > 0) {
            setErrors(found);
            hapticError();
            // Focus the first field with a problem
            const firstInvalid = ["user_name", "user_email", "message"].find((key) => found[key]);
            form.current?.elements[firstInvalid]?.focus();
            return;
        }

        sendEmail(e);
    };

    const fieldBorder = (key) =>
        errors[key]
            ? "border-b-[#944736] border-b-2 text-[#c96d58]"
            : "border-b-[#e5e5e5]";

    return (
        <>
            <div className="modal relative z-10">
                <div className="fixed inset-0 bg-[rgba(104,145,115,0.4)] transition-opacity" >
                    <div className="fixed z-10 inset-0 overflow-y-auto">
                        <motion.div initial={animateFrom} animate={animateTo} exit={animateClose} variants={dropIns} transition={{ duration: 0.5 }} className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                            <div className="modal-card my-auto relative bg-[#113023] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-[87vw] px-1 pb-2">
                                <div className="modal-text px-4 pt-5 pb-4">
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-2 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium" id="modal-title">
                                            Start a Conversation
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm">
                                                Tell me about your project, role, or collaboration. I reply within 24 hours.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-form px-4 py-3 sm:px-6">
                                    <form className='contact-form' ref={form} onSubmit={handleSubmit} noValidate>
                                        <div>
                                            <label htmlFor="user_name" className="text-base">
                                                Full Name
                                            </label>
                                            <input
                                                id="user_name"
                                                type="text"
                                                name='user_name'
                                                value={values.user_name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                maxLength={LIMITS.name}
                                                autoComplete="name"
                                                aria-invalid={errors.user_name ? "true" : "false"}
                                                aria-describedby={errors.user_name ? "user_name-error" : undefined}
                                                placeholder="James Carter"
                                                className={`name ${errors.user_name ? "mb-1" : "mb-5"} mt-2 bg-transparent font-normal w-full h-10 flex items-center pl-3 ${fieldBorder("user_name")} border-t-0 border-r-0 border-l-0 focus:ring-0 focus:border-[#e5e5e5] placeholder:text-[#3d6b52]`}
                                            />
                                            {errors.user_name && (
                                                <p id="user_name-error" role="alert" className="mb-4 pl-3 text-sm text-[#c96d58]">
                                                    {errors.user_name}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="user_email" className="text-base">
                                                Email Address
                                            </label>
                                            <input
                                                id="user_email"
                                                type='email'
                                                name='user_email'
                                                value={values.user_email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                maxLength={LIMITS.email}
                                                autoComplete="email"
                                                inputMode="email"
                                                aria-invalid={errors.user_email ? "true" : "false"}
                                                aria-describedby={errors.user_email ? "user_email-error" : undefined}
                                                placeholder="james@yourcompany.com"
                                                className={`e-mail ${errors.user_email ? "mb-1" : "mb-5"} mt-2 bg-transparent font-normal w-full h-10 flex items-center pl-3 ${fieldBorder("user_email")} border-t-0 border-r-0 border-l-0 focus:ring-0 focus:border-[#e5e5e5] placeholder:text-[#3d6b52]`}
                                            />
                                            {errors.user_email && (
                                                <p id="user_email-error" role="alert" className="mb-4 pl-3 text-sm text-[#c96d58]">
                                                    {errors.user_email}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="message" className="text-base">
                                                How can I help?
                                            </label>
                                            <textarea
                                                id='message'
                                                name='message'
                                                value={values.message}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                maxLength={LIMITS.message}
                                                aria-invalid={errors.message ? "true" : "false"}
                                                aria-describedby={errors.message ? "message-error" : undefined}
                                                placeholder="Tell me about your project, challenge, or the role you're hiring for."
                                                className={`${errors.message ? "mb-1" : "mb-5"} mt-2 bg-transparent focus:outline-none focus:border-[#e5e5e5] focus:ring-transparent font-normal w-full min-h-[6rem] pl-3 ${errors.message ? "border-[#944736] border-b-2 text-[#c96d58]" : "border-[#e5e5e5]"} border-t-0 border-r-0 border-l-0 resize-none placeholder:text-[#3d6b52]`}
                                            />
                                            {errors.message && (
                                                <p id="message-error" role="alert" className="mb-4 pl-3 text-sm text-[#c96d58]">
                                                    {errors.message}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className="w-full inline-flex justify-center border border-transparent rounded-md shadow-sm px-4 py-2 bg-[rgba(77,234,167,0.9)] hover:border-[#e5e5e5] hover:bg-transparent focus:border-[rgba(77,234,167,0.9)] text-lg font-semibold tracking-[0.5px] text-gray-700 hover:text-white sm:w-full sm:text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {
                                                isLoading ? <Spinner /> : 'Send Message'
                                            }
                                        </button>
                                    </form>
                                </div>
                                <button className="modal-close-btn cursor-pointer absolute top-0 right-0 mt-4 mr-5 focus:outline-none" onClick={openBtn} aria-label="close modal">
                                    <VscClose size='2rem' className='close-btn border focus:bg-[#4deaa7] rounded-[100%] p-[5px] hover:border-[#4deaa7] hover:bg-[#4deaa7] hover:text-[#113023] focus:border-[#4deaa7]' tabIndex='0' />
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default FormModal;